import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OffersBlock from "@/components/seo/OffersBlock";
import SeoSiloLinks from "@/components/seo/SeoSiloLinks";
import PopularPagesBlock from "@/components/seo/PopularPagesBlock";
import RecentPagesBlock from "@/components/seo/RecentPagesBlock";
import EditorialEnrichment from "@/components/seo/EditorialEnrichment";
import Breadcrumbs from "@/components/Breadcrumbs";
import LastUpdated from "@/components/LastUpdated";
import { Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { Tv, Smartphone, Zap, Shield, Clock, Star, ArrowRight, ChevronRight, Clapperboard, Globe, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TestRequestFormWithCountries from "@/components/TestRequestFormWithCountries";
import FloatingTestCTA from "@/components/FloatingTestCTA";
import { useSeoPageView } from "@/hooks/useSeoPageView";
import { buildArticleSchema, buildBreadcrumbJsonLd, organizationSchema } from "@/lib/seo-schemas";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const StarRating = ({ rating }: { rating: number }) => {
  const stars = Math.round(rating / 2);
  return (
    <div className="flex items-center gap-1.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`w-4 h-4 ${s <= stars ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/30"}`} />
      ))}
      <span className="text-sm text-muted-foreground ml-1">{rating.toFixed(1)}/10</span>
    </div>
  );
};

const SeriesPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: series, isLoading } = useQuery({
    queryKey: ["series", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("series")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  // Fetch 3 related series for "Voir aussi"
  const { data: relatedSeries } = useQuery({
    queryKey: ["series-related", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("series")
        .select("slug, title, genres, air_date, vote_average, season_number")
        .eq("published", true)
        .neq("slug", slug!)
        .order("created_at", { ascending: false })
        .limit(3);
      return data || [];
    },
    enabled: !!slug,
  });

  // ✅ Hook must be called before any conditional return (Rules of Hooks)
  useSeoPageView("series", slug, series?.title);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!series) return <NotFound />;

  const isReleased = series.air_date ? new Date(series.air_date) <= new Date() : true;
  const seasonLabel = series.season_number ? ` Saison ${series.season_number}` : "";
  const episodeLabel = series.episode_number ? ` Épisode ${series.episode_number}` : "";
  const overview = (series as any).overview as string | null;
  const genres = (series as any).genres as string | null;
  const voteAverage = (series as any).vote_average as number | null;
  const backdropUrl = (series as any).backdrop_url as string | null;
  const tagline = (series as any).tagline as string | null;
  const originalTitle = (series as any).original_title as string | null;
  const originalLanguage = (series as any).original_language as string | null;
  const director = (series as any).director as string | null;
  const castList = (series as any).cast_list as string | null;

  const synopsisSnippet = overview ? overview.substring(0, 120) + "..." : "";
  const title = isReleased
    ? `${series.title}${seasonLabel}${episodeLabel} streaming VF HD – Regarder la série complète`
    : `${series.title}${seasonLabel} – Date de sortie & infos streaming VF`;
  const description = overview
    ? `${series.title}${seasonLabel} en streaming VF HD et 4K. ${synopsisSnippet} Sur RoiDuStreaming.`
    : isReleased
      ? `Regardez ${series.title}${seasonLabel}${episodeLabel} en streaming VF HD et 4K. Série disponible en illimité sur RoiDuStreaming.`
      : `${series.title}${seasonLabel} arrive bientôt ! Préparez-vous à la regarder en streaming VF HD sur RoiDuStreaming.`;
  const canonicalUrl = `https://roidustreaming.fr/series/${series.slug}`;
  const posterImage = series.poster_url || backdropUrl || "https://roidustreaming.fr/images/default.jpg";
  const imageAlt = `Affiche de la série ${series.title}${seasonLabel}`;

  const airDateFormatted = series.air_date
    ? new Date(series.air_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": series.episode_number ? "TVEpisode" : "TVSeries",
    name: `${series.title}${seasonLabel}${episodeLabel}`,
    description: overview || description,
    image: series.poster_url,
    url: canonicalUrl,
    ...(series.season_number && { partOfSeason: { "@type": "TVSeason", seasonNumber: series.season_number } }),
    ...(series.episode_number && { episodeNumber: series.episode_number }),
    ...(genres && { genre: genres.split(", ") }),
    ...(voteAverage && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: voteAverage,
        bestRating: 10,
        ratingCount: 1,
      },
    }),
    provider: {
      "@type": "Organization",
      name: "RoiDuStreaming",
      url: "https://roidustreaming.fr",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: `Comment regarder ${series.title} en streaming VF ?`, acceptedAnswer: { "@type": "Answer", text: `Abonnez-vous à RoiDuStreaming à partir de 5€/mois ou testez gratuitement pendant 24h. Accédez à ${series.title} et +25 000 séries en VF et VOSTFR, toutes saisons et épisodes inclus.` } },
      { "@type": "Question", name: `Les nouveaux épisodes de ${series.title} sont-ils disponibles rapidement ?`, acceptedAnswer: { "@type": "Answer", text: `Oui, les nouveaux épisodes de ${series.title} sont ajoutés très rapidement après leur diffusion originale. Vous ne raterez aucun épisode.` } },
      { "@type": "Question", name: `${series.title} est-il disponible en 4K ?`, acceptedAnswer: { "@type": "Answer", text: `Oui, ${series.title} est disponible en qualité Full HD et 4K sur RoiDuStreaming, sans coupure ni publicité.` } },
      { "@type": "Question", name: `Sur quels appareils puis-je regarder ${series.title} ?`, acceptedAnswer: { "@type": "Answer", text: `${series.title} est disponible sur Smart TV, Android, iPhone, iPad, PC, Mac, Fire Stick... Installation en 5 minutes.` } },
    ]
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Séries", href: "/series" },
    { label: `${series.title}${seasonLabel}`, href: `/series/${series.slug}` },
  ];

  const advantages = [
    { icon: Tv, title: "Full HD & 4K", desc: "Qualité cinéma sur tous vos écrans" },
    { icon: Zap, title: "Streaming fluide", desc: "Zéro buffering, zéro lag" },
    { icon: Smartphone, title: "Multi-appareils", desc: "Smart TV, mobile, tablette, PC" },
    { icon: Shield, title: "Accès sécurisé", desc: "Paiement Stripe 100% sécurisé" },
    { icon: Clock, title: "Épisodes rapides", desc: "Nouveaux épisodes ajoutés très vite" },
    { icon: Star, title: "25 000+ séries", desc: "Le plus grand catalogue VF/VOSTFR" },
  ];

  const breadcrumbSchema = buildBreadcrumbJsonLd([
    { name: "Accueil", href: "/" },
    { name: "Séries", href: "/series" },
    { name: `${series.title}${seasonLabel}`, href: `/series/${series.slug}` },
  ]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="video.tv_show" />
        <meta property="og:site_name" content="RoiDuStreaming" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content={backdropUrl || posterImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={backdropUrl || posterImage} />
        {series.air_date && <meta property="article:published_time" content={`${series.air_date}T00:00:00Z`} />}
        <meta property="article:section" content="Séries" />
        {genres && genres.split(", ").map((g) => <meta key={g} property="article:tag" content={g} />)}
        <link rel="preconnect" href="https://tpujpzojoqwytuhgbwtw.supabase.co" />
        <link rel="preconnect" href="https://image.tmdb.org" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(buildArticleSchema({
          headline: `${series.title}${seasonLabel}${episodeLabel} Streaming VF HD`,
          description,
          url: canonicalUrl,
          imageUrl: backdropUrl || series.poster_url,
          datePublished: series.created_at || series.air_date || new Date().toISOString(),
          dateModified: series.created_at || new Date().toISOString(),
          section: "Séries",
        }))}</script>
      </Helmet>

      {/* ── NOSCRIPT FALLBACK : données critiques visibles sans JS ── */}
      <noscript>
        <div>
          <h1>{series.title}{seasonLabel}{episodeLabel} – Streaming VF HD</h1>
          {overview && <p>{overview.substring(0, 250)}</p>}
          {genres && <p>Genre : {genres}.</p>}
          {airDateFormatted && <p>{isReleased ? "Diffusée depuis le" : "Sortie prévue le"} {airDateFormatted}.</p>}
          {series.season_number && <p>Saison {series.season_number}{series.episode_number ? `, épisode ${series.episode_number}` : ""}.</p>}
          <p>Regardez {series.title}{seasonLabel} en streaming VF et 4K sur RoiDuStreaming, à partir de 5€/mois.</p>
          <ul>
            <li><a href="https://roidustreaming.fr">Accueil</a></li>
            <li><a href="https://roidustreaming.fr/series">Séries en streaming VF</a></li>
            <li><a href="https://roidustreaming.fr/nos-offres">Offres IPTV</a></li>
          </ul>
        </div>
      </noscript>

      <article className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs items={breadcrumbItems} />
        <div className="max-w-6xl mx-auto px-4 pt-3">
          <LastUpdated date={series.created_at || new Date().toISOString()} />
        </div>

        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center px-4 overflow-hidden">
          {(backdropUrl || series.poster_url) && (
            <img src={backdropUrl || series.poster_url!} alt={imageAlt} className="absolute inset-0 w-full h-full object-cover blur-sm scale-105" loading="eager" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />

          <div className="relative z-10 max-w-6xl mx-auto w-full flex flex-col lg:flex-row items-start gap-8 py-16">

            {/* Colonne texte */}
            <div className="flex-1 min-w-0 order-2 lg:order-1">
              <span className={`inline-block mb-4 px-4 py-1.5 rounded-full text-sm font-bold ${isReleased ? "bg-secondary/20 text-secondary border border-secondary/30" : "bg-gold/20 text-gold border border-gold/30"}`}>
                {isReleased ? "✅ Disponible maintenant" : "⏳ Bientôt disponible"}
              </span>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-4 text-foreground leading-tight">
                {series.title}{seasonLabel}{episodeLabel}
                <span className="block text-primary text-xl md:text-2xl mt-2 font-bold">en Streaming VF HD & 4K</span>
              </h1>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {genres && genres.split(", ").map((g) => (
                  <Badge key={g} variant="outline" className="border-primary/40 text-primary">{g}</Badge>
                ))}
                {voteAverage && voteAverage > 0 && <StarRating rating={voteAverage} />}
              </div>
              {series.air_date && (
                <p className="text-muted-foreground text-base mb-5">
                  📺 {isReleased ? "Diffusée depuis le" : "Sortie prévue le"} {airDateFormatted}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <TestRequestFormWithCountries
                  trigger={
                    <Button className="btn-primary text-primary-foreground font-bold py-4 px-6 sm:py-6 sm:px-8 text-sm sm:text-base rounded-full group w-full sm:w-auto">
                      🎬 Essayer gratuitement 24h
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  }
                />
                <Link to="/nos-offres" className="w-full sm:w-auto">
                  <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 font-bold py-4 px-6 sm:py-6 sm:px-8 text-sm sm:text-base rounded-full w-full">
                    Voir les offres dès 5€/mois
                  </Button>
                </Link>
              </div>
            </div>

            {/* Poster */}
            {series.poster_url && (
              <div className="shrink-0 order-1 lg:order-2 w-full lg:w-auto flex justify-center lg:justify-end">
                <img src={series.poster_url} alt={imageAlt} title={`${series.title} streaming VF`} className="w-44 sm:w-56 lg:w-72 aspect-[2/3] object-cover rounded-2xl shadow-2xl ring-2 ring-primary/30" loading="eager" width="256" height="384" />
              </div>
            )}
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-y border-border/50 bg-card/50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <span>⚡ Installation en 5 min</span>
            <span>🛟 Support 7j/7</span>
            <span>📺 +15 000 chaînes TV</span>
            <span>🔒 Paiement sécurisé</span>
          </div>
        </section>

        {/* Synopsis */}
        {overview && (
          <section className="py-12 px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-foreground">Synopsis de {series.title}</h2>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                {overview.length > 300
                  ? <>{overview.substring(0, 300)}<span className="hidden sm:inline">{overview.substring(300)}</span><span className="sm:hidden text-primary"> …</span></>
                  : overview
                }
              </p>
              {overview.length > 300 && (
                <p className="sm:hidden text-sm text-muted-foreground mt-3 leading-relaxed">
                  {overview.substring(300)}
                </p>
              )}
            </div>
          </section>
        )}

        {/* ── FICHE TECHNIQUE ── */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Fiche technique – {series.title}</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-border/40 last:border-0">
                    <th className="text-left px-4 py-3 bg-card/50 font-medium w-5/12">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm"><Tv className="w-3.5 h-3.5 shrink-0" />Titre</div>
                    </th>
                    <td className="px-4 py-3 text-foreground text-sm font-semibold">{series.title}</td>
                  </tr>
                  {originalTitle && originalTitle !== series.title && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Tv className="w-3.5 h-3.5 shrink-0" />Titre original</div></th>
                      <td className="px-4 py-3 text-foreground text-sm italic">{originalTitle}</td>
                    </tr>
                  )}
                  {series.season_number && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Tv className="w-3.5 h-3.5 shrink-0" />Saison</div></th>
                      <td className="px-4 py-3 text-foreground text-sm">Saison {series.season_number}</td>
                    </tr>
                  )}
                  {series.episode_number && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Tv className="w-3.5 h-3.5 shrink-0" />Épisode</div></th>
                      <td className="px-4 py-3 text-foreground text-sm">Épisode {series.episode_number}</td>
                    </tr>
                  )}
                  {airDateFormatted && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Clock className="w-3.5 h-3.5 shrink-0" />Date de diffusion</div></th>
                      <td className="px-4 py-3 text-foreground text-sm">{airDateFormatted}</td>
                    </tr>
                  )}
                  {genres && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Tv className="w-3.5 h-3.5 shrink-0" />Genres</div></th>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {genres.split(", ").map((g) => (
                            <span key={g} className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">{g}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  {originalLanguage && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Globe className="w-3.5 h-3.5 shrink-0" />Langue originale</div></th>
                      <td className="px-4 py-3 text-foreground text-sm">{originalLanguage}</td>
                    </tr>
                  )}
                  {director && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Clapperboard className="w-3.5 h-3.5 shrink-0" />Créateur</div></th>
                      <td className="px-4 py-3 text-foreground text-sm font-medium">{director}</td>
                    </tr>
                  )}
                  {castList && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Users className="w-3.5 h-3.5 shrink-0" />Casting</div></th>
                      <td className="px-4 py-3 text-foreground text-sm">{castList}</td>
                    </tr>
                  )}
                  {voteAverage && voteAverage > 0 && (
                    <tr className="border-b border-border/40 last:border-0">
                      <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Star className="w-3.5 h-3.5 shrink-0" />Note</div></th>
                      <td className="px-4 py-3 text-foreground text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-yellow-400">{voteAverage.toFixed(1)}</span>
                          <span className="text-muted-foreground text-xs">/ 10</span>
                          <div className="flex items-center gap-0.5">
                            {[1,2,3,4,5].map((s) => <Star key={s} className={`w-3 h-3 ${s <= Math.round(voteAverage / 2) ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/20"}`} />)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr className="border-b border-border/40 last:border-0">
                    <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Zap className="w-3.5 h-3.5 shrink-0" />Qualité</div></th>
                    <td className="px-4 py-3 text-foreground text-sm">Full HD & 4K – VF / VOSTFR</td>
                  </tr>
                  <tr className="border-b border-border/40 last:border-0">
                    <th className="text-left px-4 py-3 bg-card/50 font-medium"><div className="flex items-center gap-2 text-muted-foreground text-sm"><Shield className="w-3.5 h-3.5 shrink-0" />Disponibilité</div></th>
                    <td className="px-4 py-3 text-foreground text-sm font-semibold">{isReleased ? "✅ Disponible maintenant" : "⏳ Bientôt disponible"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── TABLE 2 : COMPATIBILITÉ APPAREILS SÉRIES ── */}
        <section className="py-6 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Regarder {series.title} sur tous vos appareils</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-primary/5">
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Appareil</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Compatibilité</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Qualité max</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { device: "Smart TV (Samsung, LG, Sony)", compat: "✅ Compatible", quality: "4K Ultra HD" },
                    { device: "Android TV / Fire Stick", compat: "✅ Compatible", quality: "Full HD" },
                    { device: "iPhone / iPad", compat: "✅ Compatible", quality: "Full HD" },
                    { device: "Android smartphone / tablette", compat: "✅ Compatible", quality: "Full HD" },
                    { device: "PC Windows / Mac", compat: "✅ Compatible", quality: "Full HD & 4K" },
                    { device: "Console (PS4, Xbox)", compat: "✅ Via navigateur", quality: "Full HD" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-border/40 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">{row.device}</td>
                      <td className="px-4 py-3 text-foreground">{row.compat}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.quality}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {genres && (
              <p className="mt-3 text-xs text-muted-foreground">
                {series.title}{seasonLabel} ({genres}) — toutes les saisons disponibles en VF et VOSTFR, sans publicité.
              </p>
            )}
          </div>
        </section>

        {/* Advantages */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3 text-foreground">
              Pourquoi regarder <span className="text-primary">{series.title}</span> sur RoiDuStreaming ?
            </h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
              {overview
                ? `Découvrez ${series.title}${genres ? `, une série ${genres}` : ""} en VF et VOSTFR. Toutes les saisons et épisodes en qualité Full HD et 4K.`
                : isReleased
                  ? `${series.title} est disponible en VF et VOSTFR. Toutes les saisons et épisodes en qualité Full HD et 4K, sur tous vos appareils.`
                  : `Dès sa diffusion, ${series.title}${seasonLabel} sera ajoutée à notre catalogue. En attendant, découvrez nos +25 000 séries !`}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {advantages.map((adv, i) => (
                <div key={i} className="p-5 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-center group">
                  <adv.icon className="w-8 h-8 mx-auto mb-3 text-primary group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-foreground mb-1">{adv.title}</h3>
                  <p className="text-sm text-muted-foreground">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-3">
              {series.title}{seasonLabel} en streaming VF sur RoiDuStreaming
            </h2>
            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base mb-4">
              Regardez <strong className="text-foreground">{series.title}{seasonLabel}{episodeLabel}</strong>{genres ? ` — ${genres}` : ""} en streaming VF et 4K sur RoiDuStreaming, sans coupure ni publicité.
              {" "}Disponible sur <Link to="/tutoriels/iptv-smart-tv-android" className="text-primary hover:underline">Smart TV</Link>, <Link to="/tutoriels/iptv-android" className="text-primary hover:underline">Android</Link>, <Link to="/tutoriels/iptv-iphone-ipad" className="text-primary hover:underline">iPhone/iPad</Link>, <Link to="/tutoriels/iptv-windows" className="text-primary hover:underline">PC</Link> et <Link to="/tutoriels/iptv-firestick" className="text-primary hover:underline">Fire Stick</Link>.
            </p>
            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
              <Link to="/nos-offres" className="text-primary hover:underline font-medium">Abonnement dès 5€/mois</Link> — +100 000 films, +15 000 chaînes TV et sport en live.
              {voteAverage && voteAverage > 0 && ` Note : ${voteAverage.toFixed(1)}/10.`}
              {" "}Lisez nos <Link to="/avis-clients" className="text-primary hover:underline">avis clients</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border border-primary/30 p-6 sm:p-8 md:p-12 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-foreground">
              {isReleased ? `Regardez ${series.title} dès maintenant !` : `Soyez prêt pour ${series.title} !`}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto text-sm sm:text-base">
              Accédez à +25 000 séries, +100 000 films et +15 000 chaînes TV. Sans engagement, à partir de 5€/mois.
            </p>
            <div className="flex justify-center w-full">
              <TestRequestFormWithCountries
                trigger={
                  <Button className="btn-primary text-primary-foreground font-bold py-4 px-6 sm:py-6 sm:px-10 text-sm sm:text-base rounded-full group w-full max-w-sm sm:w-auto">
                    🎬 Démarrer mon essai gratuit 24h
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                }
              />
            </div>
          </div>
        </section>

        {/* ── FAQ DYNAMIQUE ── */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-foreground">
              Questions fréquentes sur {series.title}
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: `Comment regarder ${series.title} en streaming VF ?`, a: `Abonnez-vous à RoiDuStreaming à partir de 5€/mois ou testez gratuitement pendant 24h. Accédez à ${series.title} et +25 000 séries en VF et VOSTFR, toutes saisons et épisodes inclus.` },
                { q: `Les nouveaux épisodes de ${series.title} sont-ils disponibles rapidement ?`, a: `Oui, les nouveaux épisodes de ${series.title} sont ajoutés très rapidement après leur diffusion originale. Vous ne raterez aucun épisode.` },
                { q: `${series.title} est-il disponible en 4K ?`, a: `Oui, ${series.title} est disponible en qualité Full HD et 4K sur RoiDuStreaming, sans coupure ni publicité.` },
                { q: `Sur quels appareils puis-je regarder ${series.title} ?`, a: `${series.title} est disponible sur Smart TV, Android, iPhone, iPad, PC, Mac, Fire Stick... Installation en 5 minutes.` },
                ...(genres ? [{ q: `Quelles séries similaires à ${series.title} sont disponibles ?`, a: `${series.title} fait partie de la catégorie ${genres}. Notre catalogue contient des milliers de séries ${genres} en VF et VOSTFR.` }] : []),
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border/50 rounded-xl px-5 bg-card/50">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ── VOIR AUSSI ── */}
        {relatedSeries && relatedSeries.length > 0 && (
          <section className="py-10 px-4 bg-card/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground mb-5">Voir aussi – Séries similaires</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {relatedSeries.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/series/${s.slug}`}
                    className="flex flex-col gap-1 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all group"
                  >
                    {s.genres && <span className="text-xs text-muted-foreground">{s.genres.split(", ")[0]}</span>}
                    <span className="font-bold text-foreground group-hover:text-primary transition-colors text-sm">
                      {s.title}{s.season_number ? ` S${s.season_number}` : ""}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      {s.vote_average && s.vote_average > 0 ? `⭐ ${(s.vote_average as number).toFixed(1)}/10` : s.air_date ? `📺 ${new Date(s.air_date).getFullYear()}` : "Streaming VF"}
                      <ChevronRight className="w-3 h-3 ml-auto" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Editorial Enrichment ── */}
        <EditorialEnrichment content={(series as any).editorial_content} />

        {/* Offers */}
        <div className="max-w-6xl mx-auto px-4">
          <OffersBlock />
        </div>

        {/* Internal Links */}
        <PopularPagesBlock />
        <RecentPagesBlock />
        <SeoSiloLinks category="series" currentSlug={slug} />

        <Footer />
        <FloatingTestCTA />
      </article>
    </>
  );
};

export default SeriesPage;
